// src/lib/tutor/active-question.ts
//
// WHICH QUESTION IS ON SCREEN RIGHT NOW.
//
// TutorFab derives the anchored question from the URL (`?questionId=`, `?q=`,
// `/question/<id>`). That works for surfaces that put the question in the URL —
// and silently fails everywhere the question lives in React state instead.
//
// `/practice/session?topic=Circles&count=all` is exactly that case: the student
// is looking at Question 1, but nothing in the URL says so. Opening Sage from
// the FAB therefore produced a panel with NO question attached — the generic
// "your AI tutor" subtitle plus the previous conversation, which reads as "Sage
// has no idea what I'm looking at". That is the 2026-07-24 report, and the
// earlier fix only covered the *Ask Tutor button* path (it passes the id
// explicitly); the FAB path stayed broken until Luttappi's browser check caught
// it on prod (2026-07-25).
//
// So: a tiny module-level channel. Question surfaces publish what's on screen;
// the FAB reads it. Deliberately NOT React context — TutorFab is mounted in the
// app shell, far above (and outside) the pages that know the question, so a
// provider would have to wrap the whole tree to solve a one-value problem.

type Listener = () => void

let activeQuestionId: string | null = null
const listeners = new Set<Listener>()

/**
 * Publish the question currently on screen, or `null` when none is (the student
 * navigated away, the session ended, the surface unmounted).
 *
 * Idempotent: re-publishing the same id does not notify, so an effect that runs
 * on every render can call this safely without causing a re-render loop.
 */
export function setActiveQuestion(id: string | null): void {
  const next = id && id.length > 0 ? id : null
  if (next === activeQuestionId) return
  activeQuestionId = next
  for (const l of listeners) l()
}

export function getActiveQuestion(): string | null {
  return activeQuestionId
}

/** Subscribe to changes. Returns an unsubscribe fn (useSyncExternalStore shape). */
export function onActiveQuestionChange(cb: Listener): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** Test-only reset so module state can't leak between cases. */
export function __resetActiveQuestion(): void {
  activeQuestionId = null
  listeners.clear()
}
